import { Link } from 'react-router-dom';
import { 
  Shield, CheckCircle, ArrowRight, CreditCard, FileText, 
  Users, Clock, TrendingUp, Star, ExternalLink, MapPin, Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Layout } from '../components/layout/Layout';

export default function Home() {
  const services = [
    {
      icon: CreditCard,
      title: 'Charge-Off Removal',
      description: 'Credit card, auto loan, and personal loan charge-offs addressed with precision.',
    },
    {
      icon: FileText,
      title: 'Collection Disputes',
      description: 'Medical, credit card, utility, apartment debt, and student loan collections challenged effectively.',
    },
    {
      icon: Users,
      title: 'Personal Info Corrections',
      description: 'Inaccurate names, addresses, phone numbers, and employment listings corrected.',
    },
    {
      icon: Shield,
      title: 'Public Records',
      description: 'Improperly reported bankruptcies and public records disputed under FCRA.',
    },
  ];

  const steps = [
    { number: '01', title: 'Sign Up', description: 'Select your package and subscribe to credit monitoring ($29/month).' },
    { number: '02', title: 'Submit Info', description: 'Complete your profile and upload required documents.' },
    { number: '03', title: 'We Work', description: 'Our team disputes inaccurate items on your behalf.' },
    { number: '04', title: 'Track Progress', description: 'Receive 35-day updates and monitor your improvement.' },
  ];

  const stats = [
    { value: '300+', label: 'Clients Served' },
    { value: '95%', label: 'Success Rate' },
    { value: '35', label: 'Day Update Cycle' },
    { value: '100%', label: 'FCRA Compliant' },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section 
        className="relative min-h-[90vh] flex items-center noise-texture"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1641849460748-7081ab1a4cef?auto=format&fit=crop&w=2000&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 hero-overlay"></div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-4 animate-fade-in-up">
              <p className="text-amber-500 font-medium">
                Tamperis Solutions LLC
              </p>
              <span className="bg-teal-600 text-white text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                Serving All 50 States
              </span>
            </div>
            <h1 className="font-heading hero-title font-bold text-white mb-6 animate-fade-in-up animation-delay-100">
              From <span className="text-amber-500">Faith</span> to{' '}
              <span className="text-teal-400">Freedom</span>
            </h1>
            <p className="text-lg text-slate-300 mb-8 animate-fade-in-up animation-delay-200">
              Restoring financial freedom through ethical, transparent, and 
              legally-compliant credit restoration services. Take control of your 
              credit journey today.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up animation-delay-300">
              <Link to="/register">
                <Button 
                  size="lg" 
                  className="bg-amber-600 hover:bg-amber-700 text-white btn-active"
                  data-testid="hero-cta-btn"
                >
                  Start Your Journey
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a 
                href="https://calendly.com/tamperis94/30min" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white text-white hover:bg-white/10 gap-2"
                  data-testid="hero-book-call-btn"
                >
                  <Calendar className="h-5 w-5" />
                  Book a Free Call
                </Button>
              </a>
            </div>

            {/* Trust Badges */}
            <div className="mt-12 flex flex-wrap gap-6 animate-fade-in-up animation-delay-400">
              <div className="flex items-center gap-2 text-slate-300">
                <CheckCircle className="h-5 w-5 text-teal-400" />
                <span className="text-sm">FCRA Compliant</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <CheckCircle className="h-5 w-5 text-teal-400" />
                <span className="text-sm">Metro 2® Certified</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <CheckCircle className="h-5 w-5 text-teal-400" />
                <span className="text-sm">CROA Compliant</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white" data-testid="stats-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="font-heading text-4xl md:text-5xl font-bold text-slate-900 stat-number">
                  {stat.value}
                </p>
                <p className="text-slate-600 mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-slate-50" data-testid="services-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-amber-700 font-medium mb-2">Our Services</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Credit Items We Address
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We focus on challenging inaccurate, outdated, incomplete, or unverifiable 
              information on your credit report.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <Card key={index} className="card-hover border-slate-200 bg-white">
                <CardContent className="p-6">
                  <div className="service-icon mb-4">
                    <service.icon className="h-8 w-8 text-amber-700" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 text-sm">
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button variant="outline" className="border-slate-900 text-slate-900">
                View All Services
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 bg-white" data-testid="how-it-works-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-amber-700 font-medium mb-2">Simple Process</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              How It Works
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our straightforward process makes credit restoration easy and transparent.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="text-6xl font-heading font-bold text-slate-100 mb-4">
                  {step.number}
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                  {step.title}
                </h3>
                <p className="text-slate-600 text-sm">
                  {step.description}
                </p>
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 right-0 w-full h-0.5 bg-gradient-to-r from-amber-500 to-transparent"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section 
        className="py-24 bg-slate-900 text-white"
        data-testid="trust-section"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-amber-500 font-medium mb-2">Why Choose Us</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                Integrity, Compliance & Empowerment
              </h2>
              <p className="text-slate-300 mb-8">
                At Tamperis Solutions LLC, we stand for integrity, compliance, and empowerment. 
                Our goal is to help clients walk confidently toward financial freedom by 
                restoring accurate and fair credit reporting.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-teal-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold">FCRA Compliance</h4>
                    <p className="text-sm text-slate-400">Full compliance with the Fair Credit Reporting Act</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-teal-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold">Metro 2® Standards</h4>
                    <p className="text-sm text-slate-400">Industry-standard reporting format for accurate disputes</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-teal-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold">Transparent Communication</h4>
                    <p className="text-sm text-slate-400">35-day update cycles to keep you informed</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1758519288905-38b7b00c1023?auto=format&fit=crop&w=800&q=80"
                alt="Diverse professional handshake"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-amber-600 text-white p-6 rounded-xl shadow-xl">
                <p className="text-3xl font-bold font-heading">35</p>
                <p className="text-sm">Day Update Cycle</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Client Success Stories Preview */}
      <section className="py-24 bg-white" data-testid="testimonials-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-amber-700 font-medium mb-2">Real Results</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Client Success Stories
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              See how our clients have achieved their dreams of homeownership and financial freedom 
              through our credit restoration services.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Lynn's Success Preview */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl overflow-hidden shadow-xl">
              <div className="relative h-48 overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_faith2freedom/artifacts/8v2t7iuw_Lynn%20Car%20and%20Funding%20Testimony.PNG"
                  alt="Lynn's Success"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: 'center 40%', transform: 'scale(1.3)' }}
                />
              </div>
              <div className="p-6 text-white">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <h3 className="font-heading text-lg font-bold mb-2">$20K Funding & New Vehicle</h3>
                <p className="text-slate-400 text-sm">Credit restored, Infiniti QX60 financed</p>
              </div>
            </div>

            {/* Homeowner Preview */}
            <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl overflow-hidden shadow-xl border border-slate-200">
              <div className="relative h-48 overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_faith2freedom/artifacts/wmz0k6a4_B%20House%20Outside.JPG"
                  alt="Dream Home"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: 'center 40%' }}
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-amber-500 text-amber-500" />
                  ))}
                </div>
                <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">Dream Home Achieved</h3>
                <p className="text-slate-600 text-sm">Credit restored, mortgage approved</p>
              </div>
            </div>

            {/* Auto Loan Preview */}
            <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl overflow-hidden shadow-xl border border-slate-200">
              <div className="relative h-48 overflow-hidden bg-slate-100">
                <img 
                  src="https://customer-assets.emergentagent.com/job_faith2freedom/artifacts/u4qnuz0t_Jasper%20car.PNG"
                  alt="Dream Car"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-amber-500 text-amber-500" />
                  ))}
                </div>
                <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">Dream Car Financed</h3>
                <p className="text-slate-600 text-sm">Credit restored, auto loan approved</p>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/testimonials">
              <Button size="lg" variant="outline" className="border-slate-900 text-slate-900">
                View All Success Stories
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
    </Layout>
  );
}
